import React from 'react';
import {
  CheckboxCard,
  HelperFields,
  GateQuestion,
  FreeTextField,
} from '../../components/soknad';
import SummaryStep from './SummaryStep';
import IntroStep from './IntroStep';
import SubjectConfirmStep from './SubjectConfirmStep';

/**
 * YesNoQuestion - Simple yes/no toggle for work impact questions
 */
function YesNoQuestion({ id, question, answer, onAnswer }) {
  return (
    <div className="mb-4">
      <p className="text-base text-text-primary mb-3">{question}</p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => onAnswer(id, true)}
          className={`
            flex-1 py-3 px-4 rounded-lg
            font-medium text-sm
            transition-all duration-200
            ${answer === true
              ? 'bg-sage-500 text-white'
              : 'bg-background-card border border-border-light text-text-primary hover:border-sage-300'
            }
          `}
        >
          Ja
        </button>
        <button
          type="button"
          onClick={() => onAnswer(id, false)}
          className={`
            flex-1 py-3 px-4 rounded-lg
            font-medium text-sm
            transition-all duration-200
            ${answer === false
              ? 'bg-sage-500 text-white'
              : 'bg-background-card border border-border-light text-text-primary hover:border-sage-300'
            }
          `}
        >
          Nei
        </button>
      </div>
    </div>
  );
}

/**
 * WizardStep - Generic step renderer
 *
 * Renders a single wizard step based on the step configuration.
 * Handles:
 * - Gate questions (yes/no that can skip content)
 * - Checkbox items
 * - Support step with sections (special format)
 * - Helper fields (who + hours)
 * - Free text field
 * - Summary step with generation
 */
export function WizardStep({
  step,
  stageData,
  helperOptions,
  onToggleCheckbox,
  onSetFreeText,
  onSetHelperFields,
  onSetGateAnswer,
  onSetConditionalData,
  // Summary step props
  config,
  allStageData,
  onEditStep,
  onGenerate,
  isGenerating,
  generateError,
  // Subject (person-picker) props
  subject,
  onConfirmSubjectFacts,
  onSubjectBlockedChange,
  onBackToPicker,
  onAdvance,
}) {
  if (!step) return null;

  const {
    title,
    instruction,
    gateQuestion,
    checkboxItems,
    hasHelperFields,
    hasFreeText,
    freeTextPlaceholder,
    freeTextLabel,
    isSummaryStep,
    isSupportStep,
    isIntroStep,
    isSubjectConfirmStep,
    sections,
  } = step;

  // Intro step (index 0) — welcome + the draft's child.
  if (isIntroStep) {
    return (
      <IntroStep
        step={step}
        subject={subject}
        onAdvance={onAdvance}
      />
    );
  }

  // Tier-2 confirm-facts step — only reached when the subject is NOT the
  // onboarded child (the wizard skips it otherwise).
  if (isSubjectConfirmStep) {
    return (
      <SubjectConfirmStep
        step={step}
        subject={subject}
        onConfirmSubjectFacts={onConfirmSubjectFacts}
        onBlockedChange={onSubjectBlockedChange}
        onBackToPicker={onBackToPicker}
      />
    );
  }

  // Determine if content should be hidden due to gate question
  const shouldSkipContent = gateQuestion?.skipOnYes
    ? stageData?.gateAnswer === true
    : stageData?.gateAnswer === false;

  // For summary step, use the dedicated SummaryStep component
  if (isSummaryStep) {
    return (
      <SummaryStep
        config={config}
        stageData={allStageData}
        onEditStep={onEditStep}
        onGenerate={onGenerate}
        isGenerating={isGenerating}
        generateError={generateError}
      />
    );
  }

  // For support step, render sections
  if (isSupportStep && sections) {
    return (
      <div>
        <h2 className="text-2xl font-semibold text-text-primary mb-2">
          {title}
        </h2>
        <p className="text-text-secondary mb-6">{instruction}</p>

        {sections.map((section, sectionIndex) => (
          <div
            key={section.id}
            className={sectionIndex > 0 ? 'mt-8 pt-6 border-t border-border-light' : ''}
          >
            <h3 className="text-lg font-semibold text-text-primary mb-1">
              {section.title}
            </h3>
            <p className="text-sm text-text-secondary mb-4">
              {section.description}
            </p>

            {/* Checkbox items in section */}
            {section.checkboxItems && (
              <div className="space-y-2 md:space-y-2">
                {section.checkboxItems.map((item) => (
                  <CheckboxCard
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    description={item.description}
                    isChecked={stageData?.checkedItems?.includes(item.id) || false}
                    onToggle={onToggleCheckbox}
                    hasFollowUp={item.hasFollowUp}
                    followUpLabel={item.followUpLabel}
                    followUpPlaceholder={item.followUpPlaceholder}
                    followUpValue={stageData?.conditionalData?.[item.id]}
                    onFollowUpChange={onSetConditionalData}
                  />
                ))}
              </div>
            )}

            {/* Yes/No items in section */}
            {section.yesNoItems && (
              <div className="space-y-4">
                {section.yesNoItems.map((item) => (
                  <YesNoQuestion
                    key={item.id}
                    id={item.id}
                    question={item.question}
                    answer={stageData?.conditionalData?.[item.id]}
                    onAnswer={onSetConditionalData}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Free text field */}
        {hasFreeText && (
          <FreeTextField
            value={stageData?.freeText || ''}
            onChange={onSetFreeText}
            placeholder={freeTextPlaceholder}
            label={freeTextLabel}
          />
        )}
      </div>
    );
  }

  // Standard step rendering
  return (
    <div>
      {/* Step header */}
      <h2 className="text-2xl font-semibold text-text-primary mb-2">
        {title}
      </h2>
      <p className="text-text-secondary mb-6">{instruction}</p>

      {/* Gate question (if present) */}
      {gateQuestion && (
        <GateQuestion
          question={gateQuestion.text}
          answer={stageData?.gateAnswer}
          onAnswer={onSetGateAnswer}
          skipOnYes={gateQuestion.skipOnYes}
          skipMessage={gateQuestion.skipMessage}
        />
      )}

      {/* Main content (hidden if gate question skips it) */}
      {(!gateQuestion || !shouldSkipContent) && (
        <div className={gateQuestion ? 'animate-fade-in' : ''}>
          {/* Checkbox items */}
          {checkboxItems?.length > 0 && (
            <div className="space-y-3 md:space-y-2">
              {checkboxItems.map((item) => (
                <CheckboxCard
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  description={item.description}
                  isChecked={stageData?.checkedItems?.includes(item.id) || false}
                  onToggle={onToggleCheckbox}
                  hasFollowUp={item.hasFollowUp}
                  followUpLabel={item.followUpLabel}
                  followUpPlaceholder={item.followUpPlaceholder}
                  followUpValue={stageData?.conditionalData?.[item.id]}
                  onFollowUpChange={onSetConditionalData}
                />
              ))}
            </div>
          )}

          {/* Free text field */}
          {hasFreeText && (
            <FreeTextField
              value={stageData?.freeText || ''}
              onChange={onSetFreeText}
              placeholder={freeTextPlaceholder}
              label={freeTextLabel}
            />
          )}

          {/* Helper fields (who + hours) */}
          {hasHelperFields && (
            <HelperFields
              helperValue={stageData?.primaryHelper || ''}
              hoursValue={stageData?.estimatedHours || ''}
              onHelperChange={(helper) =>
                onSetHelperFields(helper, stageData?.estimatedHours || '')
              }
              onHoursChange={(hours) =>
                onSetHelperFields(stageData?.primaryHelper || '', hours)
              }
              helperOptions={helperOptions}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default WizardStep;
